'use client';
import React, { useState } from 'react';
import Button from '../Button/Button';
import CloseIcon from '../Icons/Action/CloseIcon';
import { T, useTranslate } from '@tolgee/react';
import CalendlyModal from '@/components/Modal/CalendlyModal';
import { closeCalendlyOnLimitModal, openCalendlyOnLimitModal } from '@/redux/reducers/modalSlice';
import { useDispatch } from 'react-redux';
import { useUser } from '@clerk/nextjs';

interface LimitModalProps {
  isOpen: boolean;
  onClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const LimitModal: React.FC<LimitModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [isCalendlyModalOpen, setCalendlyModalOpen] = useState(false);

  const openCalendlyModal = () => {
    setCalendlyModalOpen(true);
    dispatch(openCalendlyOnLimitModal());
  };

  const closeCalendlyModal = () => {
    setCalendlyModalOpen(false);
    dispatch(closeCalendlyOnLimitModal());
  };

  const { user } = useUser();
  const calendlyUrl = process.env.NEXT_PUBLIC_ORIENTATION_CALL_URL;
  const userId = user?.id ?? '';
  const fullUrl = `${calendlyUrl}?utm_campaign=${userId}`;
  const { t: _t } = useTranslate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      {isCalendlyModalOpen ? (
        <CalendlyModal isOpen={isCalendlyModalOpen} onClose={closeCalendlyModal} url={fullUrl} />
      ) : (
        <div className="bg-white w-full max-w-4xl p-6 rounded-2xl relative shadow-lg">
          <button
            onClick={(e) => onClose(e)}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 focus:outline-hidden"
          >
            <CloseIcon />
          </button>
          <h2 className="text-4xl text-center mb-6">
            <T>booking_flow.no_booking_possible.your_options_section.title.label</T>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* First Option */}
            <div
              className="p-6 bg-primary rounded-2xl shadow-sm text-white relative"
              style={{
                backgroundImage: "url('/img/modal/pattern0.svg')",
                backgroundBlendMode: 'overlay',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <span className="bg-white rounded-full text-black text-center p-4 w-5 h-5 flex items-center justify-center">
                1
              </span>
              <h3 className="text-lg mb-4 mt-5">
                <T>
                  booking_flow.no_booking_possible.your_options_section.orientation_call.title.label
                </T>
              </h3>
              <div className="mb-32 text-sm font-poppins">
                <ul className="eap-bullet-list">
                  <li>
                    <T>
                      booking_flow.no_booking_possible.your_options_section.orientation_call.bullet1.label
                    </T>
                  </li>
                  <li>
                    <T>
                      booking_flow.no_booking_possible.your_options_section.orientation_call.bullet2.label
                    </T>
                  </li>
                  <li>
                    <T>
                      booking_flow.no_booking_possible.your_options_section.orientation_call.bullet3.label
                    </T>
                  </li>
                </ul>
              </div>
              <div className="absolute bottom-0 left-0 w-full p-5">
                <Button onClick={openCalendlyModal}>
                  <T>
                    booking_flow.no_booking_possible.your_options_section.orientation_call.button.book_orientation_call.label
                  </T>
                </Button>
              </div>
            </div>

            {/* Second Option */}
            <div
              className="p-6 bg-primary rounded-2xl shadow-sm text-white relative"
              style={{
                backgroundImage: "url('/img/modal/pattern1.svg')",
                backgroundBlendMode: 'overlay',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <span className="bg-black rounded-full text-center p-4 w-5 h-5 flex items-center justify-center">
                2
              </span>
              <h3 className="text-lg mb-4 mt-5">
                <T>
                  booking_flow.no_booking_possible.your_options_section.contact_bloom.title.label
                </T>
              </h3>
              <p className="mb-12 text-sm font-poppins">
                <T>
                  booking_flow.no_booking_possible.your_options_section.contact_bloom.text.label
                </T>
              </p>
              <div className="absolute bottom-0 left-0 w-full p-5">
                <Button href="mailto:health@getbloom.work">
                  <T>
                    booking_flow.no_booking_possible.your_options_section.contact_bloom.button.send_message.label
                  </T>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LimitModal;
